import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authFetch, API_BASE } from '../services/api/client';

export interface Property {
  id: string;
  name: string;
  units: number;
  readOnly?: boolean;
}

const READ_ONLY_PROPERTY_IDS = new Set<string>(['quinley_nashville']);

const applyReadOnly = (p: Property): Property =>
  p.readOnly || READ_ONLY_PROPERTY_IDS.has(p.id) ? { ...p, readOnly: true } : p;

const createSyntheticProperty = (id: string): Property =>
  applyReadOnly({ id, name: id, units: 0, readOnly: true });

interface PropertyContextValue {
  property: Property | null;
  properties: Property[];
  isLoading: boolean;
  error: string | null;
  setProperty: (property: Property) => void;
  setPropertyById: (id: string) => void;
  refreshProperties: () => Promise<void>;
}

const PropertyContext = createContext<PropertyContextValue | null>(null);

// Default property if none selected
const DEFAULT_PROPERTY_ID = 'soundry';
const STORAGE_KEY = 'canopy:preferredProperty';

// Last list /api/properties returned from the live registry. It paints the
// selector before the API answers and stands in when the API fails, so an
// outage never shrinks the selector to a hand-maintained list that has drifted
// out of date (that list dropped Conway, Grove, Magnolia and Quinley).
const LIST_STORAGE_KEY = 'canopy:propertyList';
const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000, 60000];

const getStoredPropertyList = (): Property[] => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(LIST_STORAGE_KEY) || 'null');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p.id === 'string' && typeof p.name === 'string')
      .map((p) =>
        applyReadOnly({
          id: p.id,
          name: p.name,
          units: Number(p.units) || 0,
          readOnly: Boolean(p.readOnly),
        }),
      );
  } catch (error) {
    console.warn('Failed to read cached property list:', error);
    return [];
  }
};

const setStoredPropertyList = (list: Property[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify(list));
  } catch (error) {
    console.warn('Failed to cache property list:', error);
  }
};

const getStoredPropertyId = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to read preferred property from storage:', error);
    return null;
  }
};

interface PropertyProviderProps {
  children: ReactNode;
}

export function PropertyProvider({ children }: PropertyProviderProps) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState<Property[]>(getStoredPropertyList);
  const [property, setPropertyState] = useState<Property | null>(() => {
    const urlPropertyId = searchParams.get('property')?.toLowerCase().trim();
    const storedPropertyId = getStoredPropertyId();
    const initialId = urlPropertyId || storedPropertyId || DEFAULT_PROPERTY_ID;
    const cached = getStoredPropertyList().find((p) => p.id === initialId);
    if (cached) return cached;
    if (urlPropertyId) return createSyntheticProperty(urlPropertyId);
    // First visit (nothing cached yet): a placeholder keeps the app off the
    // full-screen loader until /api/properties answers.
    return applyReadOnly({ id: initialId, name: initialId, units: 0 });
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const propertyRef = useRef<Property | null>(null);
  const userRef = useRef<typeof user>(null);
  const lastRefreshUserKeyRef = useRef<string | null>(null);
  const userSelectionRef = useRef(false);
  const syncingToUrlRef = useRef(false);
  const searchParamsRef = useRef(searchParams);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const refreshPropertiesRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    propertyRef.current = property;
  }, [property]);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    userSelectionRef.current = false;
    // Don't reset property - keep current selection
    // Properties will refresh via the separate effect
  }, [user?.uid]);

  const setStoredPropertyId = (propertyId: string) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, propertyId);
    } catch (error) {
      console.warn('Failed to store preferred property:', error);
    }
  };

  // Debounced API call to avoid hammering server on rapid changes
  const pendingUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updatePreferredProperty = useCallback((propertyId: string) => {
    if (!userRef.current) return;
    // Cancel any pending update
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
    }
    // Debounce: wait 1s before actually calling API
    pendingUpdateRef.current = setTimeout(async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/me`, {
          method: 'PATCH',
          body: JSON.stringify({ preferred_property: propertyId }),
        });
        if (!res.ok) {
          console.warn('Failed to update preferred property:', res.status);
        }
      } catch (error) {
        console.warn('Failed to update preferred property:', error);
      }
    }, 1000);
  }, []);

  const getUrlPropertyId = () => {
    const params = searchParamsRef.current;
    if (!params) return null;
    const propertyParam = params.get('property');
    if (!propertyParam) return null;
    return propertyParam.toLowerCase().trim();
  };

  const resolveDefaultProperty = (loadedProperties: Property[]) => {
    if (loadedProperties.length === 0) return null;
    return loadedProperties.find((p) => p.id === DEFAULT_PROPERTY_ID) || loadedProperties[0];
  };

  const resolveInitialProperty = (loadedProperties: Property[]) => {
    const currentProperty = propertyRef.current;
    if (currentProperty) {
      const inList = loadedProperties.find((p) => p.id === currentProperty.id);
      if (inList) return inList;
    }

    const urlPropertyId = getUrlPropertyId();
    if (urlPropertyId) {
      const fromUrl = loadedProperties.find((p) => p.id === urlPropertyId);
      if (fromUrl) return fromUrl;
      return createSyntheticProperty(urlPropertyId);
    }

    const currentUser = userRef.current;
    if (!userSelectionRef.current && currentUser?.preferredProperty) {
      const preferred = loadedProperties.find((p) => p.id === currentUser.preferredProperty);
      if (preferred) return preferred;
    }

    const storedPropertyId = getStoredPropertyId();
    if (storedPropertyId) {
      const stored = loadedProperties.find((p) => p.id === storedPropertyId);
      if (stored) return stored;
    }

    return resolveDefaultProperty(loadedProperties);
  };

  const applyLoadedProperties = (loadedProperties: Property[]) => {
    setProperties(loadedProperties);
    const nextProperty = resolveInitialProperty(loadedProperties);
    if (nextProperty) {
      setPropertyState(nextProperty);
    }
  };

  // Load properties from API. On failure keep the list already on screen (the
  // last good one when we have it) and retry, rather than inventing a list.
  const refreshProperties = useCallback(async () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setIsLoading(true);
    setError(null);

    let loaded = false;
    try {
      const res = await authFetch(`${API_BASE}/api/properties?include_watch_only=true`);
      if (!res.ok) {
        throw new Error(`Failed to load properties: ${res.status}`);
      }

      const data = await res.json();
      if (!Array.isArray(data?.properties)) {
        throw new Error('Failed to load properties: response had no property list');
      }
      const loadedProperties: Property[] = data.properties.map((p: any) =>
        applyReadOnly({
          id: p.id,
          name: p.name,
          units: p.unit_count || 0,
          readOnly: Boolean(p.read_only),
        }),
      );

      if (data.config_source === 'fallback') {
        // This API instance hasn't loaded the live registry yet and answered
        // with its short emergency roster. Only show that if we have nothing better.
        if (getStoredPropertyList().length === 0) {
          applyLoadedProperties(loadedProperties);
        }
        throw new Error('Failed to load properties: server is still loading its property registry');
      }

      setStoredPropertyList(loadedProperties);
      applyLoadedProperties(loadedProperties);
      loaded = true;
    } catch (err) {
      console.error('Failed to load properties:', err);
      setError(err instanceof Error ? err.message : 'Failed to load properties');
    } finally {
      setIsLoading(false);
    }

    if (loaded) {
      retryAttemptRef.current = 0;
      return;
    }
    const delay = RETRY_DELAYS_MS[Math.min(retryAttemptRef.current, RETRY_DELAYS_MS.length - 1)];
    retryAttemptRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      void refreshPropertiesRef.current();
    }, delay);
  }, []);

  useEffect(() => {
    refreshPropertiesRef.current = refreshProperties;
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [refreshProperties]);

  useEffect(() => {
    const refreshKey = user?.uid ?? '__anon__';
    if (lastRefreshUserKeyRef.current === refreshKey) return;
    lastRefreshUserKeyRef.current = refreshKey;
    void refreshProperties();
  }, [user?.uid, refreshProperties]);

  useEffect(() => {
    if (!property) return;
    setStoredPropertyId(property.id);
  }, [property?.id]);

  // Sync property to URL - only when property changes, not on every URL change
  useEffect(() => {
    if (!property) return;
    const currentParam = searchParams.get('property');
    if (currentParam === property.id) return;

    // Flag that we're syncing state → URL (prevent reverse sync)
    syncingToUrlRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.set('property', property.id);
    setSearchParams(next, { replace: true });

    // Clear flag after a tick to allow future URL-initiated changes
    requestAnimationFrame(() => {
      syncingToUrlRef.current = false;
    });
  }, [property?.id, searchParams, setSearchParams]);

  // Only react to URL property changes when user navigates with different property in URL
  useEffect(() => {
    // Skip if we just synced state → URL (prevents circular update)
    if (syncingToUrlRef.current) return;

    const urlPropertyId = searchParams.get('property');
    if (!urlPropertyId) return;

    const normalized = urlPropertyId.toLowerCase().trim();
    if (!normalized) return;

    // Skip if URL already matches current property
    if (normalized === propertyRef.current?.id) return;

    const found = properties.find((p) => p.id === normalized);
    if (found) {
      userSelectionRef.current = true;
      setPropertyState(found);
      updatePreferredProperty(found.id);
    } else {
      userSelectionRef.current = true;
      setPropertyState(createSyntheticProperty(normalized));
    }
    // IMPORTANT: do NOT add property?.id to deps here.
    // This effect should respond to URL changes, not state changes.
    // Use propertyRef.current for comparisons instead.
  }, [searchParams, properties, updatePreferredProperty]);

  // Set property directly
  const setProperty = useCallback(
    (newProperty: Property) => {
      if (property?.id === newProperty.id) return;
      userSelectionRef.current = true;
      setPropertyState(newProperty);
      void updatePreferredProperty(newProperty.id);
    },
    [property?.id, updatePreferredProperty],
  );

  // Set property by ID
  const setPropertyById = useCallback(
    (id: string) => {
      const found = properties.find((p) => p.id === id);
      if (found) {
        if (property?.id === found.id) return;
        userSelectionRef.current = true;
        setPropertyState(found);
        void updatePreferredProperty(found.id);
      } else {
        console.warn(`Property with id "${id}" not found`);
      }
    },
    [properties, property?.id, updatePreferredProperty],
  );

  const value: PropertyContextValue = {
    property,
    properties,
    isLoading,
    error,
    setProperty,
    setPropertyById,
    refreshProperties,
  };

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

// Hook to access property context
export function useProperty() {
  const context = useContext(PropertyContext);

  if (!context) {
    throw new Error('useProperty must be used within a PropertyProvider');
  }

  return context;
}

// Same as useProperty, but returns null when no provider is present.
// Useful when a component is rendered standalone (e.g. unit tests, dev preview).
export function useOptionalProperty() {
  return useContext(PropertyContext);
}

// Hook for components that only need the property ID (convenience)
export function usePropertyId() {
  const { property } = useProperty();
  return property?.id ?? null;
}
